import { useEffect, useRef, useState, useCallback } from "react";
import { getAlarms } from "api/alarm";

type AlarmItem = any;

export const useAlarmNotifications = (pollIntervalMs: number = 300000) => {
  const [latestAlarm, setLatestAlarm] = useState<AlarmItem | null>(null);
  const [hasUnacknowledgedAlarms, setHasUnacknowledgedAlarms] = useState<boolean>(false);
  const previousAlarmsRef = useRef<AlarmItem[]>([]);

  const fetchAndCompare = useCallback(async () => {
    try {
      const response: any = await getAlarms();
      if (response.status === 200) {
        const newData: Array<AlarmItem & { id?: number; statusId?: number }> = response.data;

        // Check if there are any unacknowledged alarms (statusId === 0) for the badge
        const hasUnacknowledged = newData.some((item) => item?.statusId === 0);
        setHasUnacknowledgedAlarms(hasUnacknowledged);

        // On first load, just store the data without showing a notification
        if (previousAlarmsRef.current.length === 0) {
          previousAlarmsRef.current = newData;
          return;
        }

        // Build a set of previous IDs for comparison
        const previousIds = new Set(
          previousAlarmsRef.current.map((item) => item.id)
        );

        // Find alarms that are new (present in newData but not in previousAlarmsRef)
        const newlyAdded = newData.filter(
          (item) => item.id != null && !previousIds.has(item.id)
        );

        // Only show a notification if there are new alarms
        if (newlyAdded.length > 0) {
          // Show the most recently added new alarm
          setLatestAlarm(newlyAdded[newlyAdded.length - 1]);
        }

        // Update stored alarms for next comparison
        previousAlarmsRef.current = newData;
      }
    } catch (e) {
      // fail silently for notifications – main table handles errors
    }
  }, []);

  useEffect(() => {
    fetchAndCompare();
    const intervalId = setInterval(() => {
      fetchAndCompare();
    }, pollIntervalMs);
    return () => clearInterval(intervalId);
  }, [fetchAndCompare, pollIntervalMs]);

  const dismissLatestAlarm = () => {
    setLatestAlarm(null);
  };

  const refreshAlarms = () => {
    fetchAndCompare();
  };

  return {
    latestAlarm,
    dismissLatestAlarm,
    hasUnacknowledgedAlarms,
    refreshAlarms,
  };
};


